<script setup lang='ts'>
import { LOAD_STATUS, MEDIA_MD, homePath } from '@qcwp/common'
import type { CountdownProps } from 'naive-ui'
import { useTestDriveComponent } from '~~/src/composables/useCarTestDrive'
import type { ArticleCarRefVO } from '~~/src/server/modules/article/type'

const props = withDefaults(defineProps<{
  hiddenTitle?: boolean
  carList: ArticleCarRefVO[]
}>(), {
  hiddenTitle: false,
  carList: () => [],
})
const { carList } = toRefs(props)

const currentQuicklySelectIndex = ref(carList.value.findIndex(v => v.carSeriesId && v.carSeriesId !== '0') > 0
  ? carList.value.findIndex(v => v.carSeriesId && v.carSeriesId !== '0')
  : 0)
const currentQuicklySelectCar = computed(() => carList.value[currentQuicklySelectIndex.value])

const mediaOptions = computed<{
  labelWidth: number
  cols: number
  labelPlacement: 'left' | 'top'
}>(() => {
  return (MEDIA_MD.value)
    ? {
        labelWidth: 120,
        labelPlacement: 'left',
        cols: 2,
      }
    : {
        labelWidth: 70,
        labelPlacement: 'top',
        cols: 1,
      }
})

const renderCountdown: CountdownProps['render'] = ({ seconds }) => {
  return `${String(seconds)}s `
}

const {
  timeOptions,
  rules,
  formRef,
  seriesFormItemRef,
  areaFormItemRef,
  formValue,
  submitHandle,

  areaSelectCascaderRef,
  selected,
  selectArea,
  completeState,
  showMpQrcode,
  initHandler,
} = useTestDriveComponent()

let stop: any = [
  watch(currentQuicklySelectIndex, (index) => {
    if (index < 0)
      return
    const car = currentQuicklySelectCar.value
    if (!car)
      return
    formValue.value.carId = [car.carBrandId, car.carVendorId, car.carSeriesId, undefined]
    formValue.value.carBrandName = car.carBrandName
    formValue.value.carVendorName = car.carVendorName
    formValue.value.carSeriesName = car.carSeriesName
    // formValue.value.carModelName = car.carModelName
  }, { immediate: true }),

  watch(() => formValue.value.carId, (data) => {
    if (currentQuicklySelectIndex.value === -1)
      return

    const car = currentQuicklySelectCar.value
    if (!car || car.carBrandId !== data[0] || car.carVendorId !== data[1] || car.carSeriesId !== data[2])
      currentQuicklySelectIndex.value = -1
  }),
]

const addWatch = () => {
  stop = [
    watch(currentQuicklySelectIndex, (index) => {
      if (index < 0)
        return
      const car = currentQuicklySelectCar.value
      if (!car)
        return
      formValue.value.carId = [car.carBrandId, car.carVendorId, car.carSeriesId, undefined]
      formValue.value.carBrandName = car.carBrandName
      formValue.value.carVendorName = car.carVendorName
      formValue.value.carSeriesName = car.carSeriesName
    // formValue.value.carModelName = car.carModelName
    }, { immediate: true }),

    watch(() => formValue.value.carId, (data) => {
      if (currentQuicklySelectIndex.value === -1)
        return

      const car = currentQuicklySelectCar.value
      if (!car || car.carBrandId !== data[0] || car.carVendorId !== data[1] || car.carSeriesId !== data[2])
        currentQuicklySelectIndex.value = -1
    }),
  ]
}
const removeWatch = () => {
  stop?.forEach((s: any) => s?.())
  stop = null!
  showMpQrcode(false, true)
}
addWatch()
onActivated(addWatch)
onDeactivated(removeWatch)
onUnmounted(removeWatch)

await initHandler()
</script>

<template>
  <div class="bg-white pb-5" md="rounded-md p-3">
    <div md="w-full m-auto">
      <h3 v-if="!hiddenTitle" class="font-bold text-5 md:text-6 p-3">
        预约试驾/询价
      </h3>
      <p text-title-500 p-3 pt-0 border="b-1 gray-200 dashed">
        简单填写以下信息，轻松预约到店试驾
        <em>(我们将严格保护您的个人信息安全)</em>
      </p>
      <div v-if="carList?.length" class="p-3 pb-0 flex items-start">
        <span class="pt-1 flex-shrink-0">快速选择：</span>
        <div class="flex flex-wrap">
          <span
            v-for="v, i in carList" :key="i"
            class="px-3 py-1 rounded-sm  mr-3 mb-2"
            :class="[i === currentQuicklySelectIndex ? 'bg-slate-2 cursor-not-allowed' : 'bg-blue-5 cursor-pointer text-white']"
            :title="i === currentQuicklySelectIndex ? '当前选择' : '快速填入'"
            @click="currentQuicklySelectIndex = i"
          >
            <span v-if="!v.carSeriesName?.startsWith(v.carVendorName || v.carBrandName)">{{ v.carVendorName || v.carBrandName }}</span>
            <span>{{ v.carSeriesName }}</span>
          </span>
        </div>
      </div>
      <div class="flex justify-center px-3 pt-3" md="gap-5 items-start justify-around">
        <n-form
          ref="formRef"
          class="w-full"
          :label-width="mediaOptions.labelWidth"
          :model="formValue"
          :rules="rules"
          size="medium"
          :label-placement="mediaOptions.labelPlacement"
        >
          <n-form-item ref="seriesFormItemRef" label="意向车系/车型" required path="carId">
            <ClientOnly>
              <page-series-cascader
                class="w-full"
                :placement="MEDIA_MD ? 'left' : 'bottom'"
                :brand-id="formValue.carId[0]"
                :series-id="formValue.carId[2]"
                :model-id="formValue.carId[3]"
                :can-customize="true"
                :customize-model-name="formValue.carModelName"
                lazy
                @selected="selected"
              >
                <template #default="{ state }">
                  <div class="border-1 border-solid border-gray-300 rounded-sm relative w-full md:w-100 py-1 px-3">
                    <template v-if="state !== LOAD_STATUS.LOADING">
                      <span v-if="![formValue.carBrandName, formValue.carVendorName, formValue.carSeriesName, formValue.carModelName].filter(v => v).length" class="text-gray">请选择车系</span>
                      <template v-else>
                        <span class="text-black">{{ formValue.carVendorName ? formValue.carVendorName : formValue.carBrandName }}</span>
                        <span v-if="formValue.carVendorName || formValue.carBrandName" class="text-slate-3"> / </span>
                        <span :class="[formValue.carSeriesName ? 'text-black' : 'text-blue-5']">
                          {{ formValue.carSeriesName ? formValue.carSeriesName : '请选择车系' }}
                        </span>
                        <template v-if="formValue.carSeriesName && formValue.carModelName">
                          <span class="text-slate-3"> / </span>
                          <span class="text-black">{{ formValue.carModelName }}</span>
                        </template>
                      </template>
                    </template>
                    <template v-else>
                      <span class="text-gray">
                        <i class="i-eos-icons:three-dots-loading" />
                        <span text-3 ml-1>加载中...</span>
                      </span>
                    </template>
                    <i i-ic:outline-keyboard-arrow-down class="absolute right-2 top-50% -translate-y-50%" />
                  </div>
                </template>
              </page-series-cascader>
            </ClientOnly>
          </n-form-item>
          <n-form-item ref="areaFormItemRef" label="城市/经销商" required path="cityId">
            <ClientOnly>
              <base-area-select-cascader
                ref="areaSelectCascaderRef"
                :step="2"
                class="w-80% md:w-100"
                :placement="MEDIA_MD ? 'left' : 'bottom'"
                placeholder="请选择地区"
                :required-dealer="false"
                :series-id="formValue.carId[2]"
                :default-value="[formValue.provinceId, formValue.cityId, []]"
                default-select-dealer
                @selected="selectArea"
              />
            </ClientOnly>
          </n-form-item>
          <n-form-item label="联系人" required :show-feedback="false">
            <n-grid :cols="2" :x-gap="12">
              <n-form-item-gi path="name" :show-label="false">
                <n-input v-model:value="formValue.name" placeholder="您的姓名" />
              </n-form-item-gi>
              <n-form-item-gi path="sex" :show-label="false">
                <n-radio-group v-model:value="formValue.sex" name="sex">
                  <n-space>
                    <n-radio :value="1">
                      先生
                    </n-radio>
                    <n-radio :value="2">
                      女士
                    </n-radio>
                  </n-space>
                </n-radio-group>
              </n-form-item-gi>
            </n-grid>
          </n-form-item>
          <div md="flex items-center">
            <n-form-item label="手机号码" required path="phone">
              <n-input v-model:value="formValue.phone" placeholder="您的联系方式" />
            </n-form-item>
            <n-form-item label="试驾时间" path="time">
              <n-radio-group v-model:value="formValue.time" name="radiogroup">
                <n-space>
                  <n-radio v-for="t in timeOptions" :key="t.value" :value="t.value">
                    {{ t.label }}
                  </n-radio>
                </n-space>
              </n-radio-group>
            </n-form-item>
          </div>
          <n-form-item label="备注" path="remark">
            <n-input v-model:value="formValue.remark" type="textarea" placeholder="其他想要描述的信息" />
          </n-form-item>
        </n-form>
      </div>
      <div flex="~ col" items-center>
        <n-button type="primary" px-24 mb-3 @click="submitHandle">
          提交申请
        </n-button>
        <div>
          <span>提交即表示同意</span>
          <NuxtLink to="/docs/personnel-info-statement" no-rel target="_blank" text-primary>
            《个人信息保护声明》
          </NuxtLink>
          <span>和</span>
          <NuxtLink to="/docs/test-drive-protocol" target="_blank" text-primary>
            《试驾安全要求事项》
          </NuxtLink>
        </div>

        <div v-if="completeState.isComplete" class="cursor-pointer">
          <i class="text-blue-5 text-5 mt-3 i-ic:baseline-qrcode" @click="showMpQrcode(true, false)" />
        </div>
      </div>
      <!-- 获取定位 -->
      <!-- <ClientOnly>
        <iframe
          id="geoPage" width="0" height="0" frameborder="0" style="display: none;" scrolling="no"
          src="https://apis.map.qq.com/tools/geolocation?key=4Y2BZ-QVLEU-VARVL-BJONA-GCIG3-AYFXI&referer=qcwp"
        />
      </ClientOnly> -->
    </div>
    <n-modal v-model:show="completeState.show" :mask-closable="true" :auto-focus="false">
      <div class="bg-white rounded-md w-9/10 md:w-100  rounded-md p-3 flex flex-col items-center">
        <h3 v-if="completeState.auto" class="text-center text-green-5 text-4 font-b pt-3 flex items-center">
          <i class="i-icon-park-outline:email-successfully mr-1" />
          <span>数据提交成功</span>
        </h3>
        <img class="w-30 h-30 my-sm" src="~/assets/images/weixin-mp-qrcode.jpg" alt="">
        <p class="text-center">
          长按识别二维码
        </p>
        <p class="text-center mt-1">
          关注“汽车网评”公众号，了解最新汽车行业资讯！
        </p>
        <div class="flex items-center mt-lg text-center">
          <nuxt-link :to="homePath()" class="block w-25 py-2 rounded-md bg-green-5 text-white">
            返回首页
          </nuxt-link>
          <button class="py-2 ml-lg w-25 rounded-md bg-blue-5 text-white" @click="showMpQrcode(false, false)">
            <span v-if="completeState.auto">
              <n-countdown :render="renderCountdown" :duration="10000" :active="completeState.auto" @finish="showMpQrcode(false, false)" />
            </span>
            <span>关闭</span>
          </button>
        </div>
      </div>
    </n-modal>
  </div>
</template>
